const HORIZONTAL_CRS_TYPES = new Set([
  'PROJCS', 'PROJCRS', 'PROJECTEDCRS',
  'GEOGCS', 'GEOGCRS', 'GEOGRAPHICCRS', 'GEODCRS', 'GEODETICCRS', 'GEOCCS',
]);

// Preserve node boundaries so identifiers on units, datums and base CRSs cannot
// be mistaken for the identifier of the coordinate system itself. Quoted text
// (including doubled quotes and brackets in names) is consumed as one token.
function readWktTree(wkt) {
  const root = { values: [] };
  const stack = [root];
  const tokens = /\s*(?:"((?:[^"]|"")*)"|([^\s[\](),"]+)|([\[\](),]))/gy;
  let position = 0;

  while (position < wkt.length) {
    tokens.lastIndex = position;
    const token = tokens.exec(wkt);
    if (!token) {
      if (wkt.slice(position).trim()) return null;
      break;
    }
    position = tokens.lastIndex;
    const current = stack[stack.length - 1];
    const punctuation = token[3];

    if (punctuation === '[' || punctuation === '(') {
      const keyword = current.values.pop();
      if (typeof keyword !== 'string' || !/^[A-Za-z][A-Za-z0-9_]*$/.test(keyword)) {
        return null;
      }
      const node = {
        keyword: keyword.toUpperCase(),
        values: [],
        closing: punctuation === '[' ? ']' : ')',
      };
      current.values.push(node);
      stack.push(node);
    } else if (punctuation === ']' || punctuation === ')') {
      if (stack.length === 1 || current.closing !== punctuation) return null;
      stack.pop();
    } else if (punctuation !== ',') {
      current.values.push(token[1] !== undefined ? token[1].replace(/""/g, '"') : token[2]);
    }
  }

  return stack.length === 1 && root.values.length === 1 ? root.values[0] : null;
}

function findHorizontalCrs(node) {
  if (!node || typeof node !== 'object') return null;
  if (HORIZONTAL_CRS_TYPES.has(node.keyword)) return node;

  // A bound CRS describes its source coordinates; a compound CRS has a
  // horizontal component followed by a vertical/temporal component.
  if (node.keyword === 'BOUNDCRS') {
    const source = node.values.find(value => value?.keyword === 'SOURCECRS');
    return source?.values.map(findHorizontalCrs).find(Boolean) ?? null;
  }
  if (node.keyword === 'COMPD_CS' || node.keyword === 'COMPOUNDCRS') {
    return node.values.map(findHorizontalCrs).find(Boolean) ?? null;
  }
  return null;
}

/** Return the horizontal CRS's own EPSG authority in WKT1 or WKT2, or null. */
export function extractEpsgFromWkt(wkt) {
  if (typeof wkt !== 'string' || !wkt.trim()) return null;
  const crs = findHorizontalCrs(readWktTree(wkt));
  if (!crs) return null;

  for (const value of crs.values) {
    if (value?.keyword !== 'AUTHORITY' && value?.keyword !== 'ID') continue;
    const [authority, code] = value.values;
    if (typeof authority !== 'string' || authority.toUpperCase() !== 'EPSG') continue;
    if (typeof code !== 'string' || !/^\d+$/.test(code)) continue;
    const epsg = Number(code);
    if (Number.isSafeInteger(epsg) && epsg > 0) return epsg;
  }
  return null;
}
